import React, {useCallback, useState} from 'react'
import {Pressable, StyleSheet, View} from 'react-native'
import {useNavigation} from '@react-navigation/native'

import {presenceApiClient} from '#/lib/api/presence/client'
import {useOnlineUsers} from '#/state/chat-bubble/presence'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {useRooms} from '#/screens/Rooms/hooks/useRooms'

export function PrivateRoomButton({
  recipientDid,
}: {
  recipientDid: string
}) {
  const t = useTheme()
  const navigation = useNavigation<any>()
  const {currentAccount} = useSession()
  const {users} = useOnlineUsers()
  const {createRoom} = useRooms()
  const [myRoomSlug, setMyRoomSlug] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  // Check if the recipient has an active private room
  const recipient = users.find(u => u.did === recipientDid)
  const theirRoomSlug = recipient?.privateRoomSlug

  const handleStartRoom = useCallback(async () => {
    if (!currentAccount?.did || isCreating) return

    setIsCreating(true)
    try {
      // Create a private room via the rooms API
      const room = await createRoom(
        `Private: ${recipient?.handle || 'chat'}`,
        undefined,
        true,
      )

      // Set the private room slug in presence
      await presenceApiClient.setPrivateRoom(currentAccount.did, room.id)
      setMyRoomSlug(room.id)

      // Navigate to the p2p call (room.id is "p2p-xxx", extract the ID part)
      navigation.navigate('P2pCall', {
        callId: room.id.replace('p2p-', ''),
      })
    } catch (err) {
      console.error('Failed to create private room:', err)
    } finally {
      setIsCreating(false)
    }
  }, [
    currentAccount?.did,
    isCreating,
    createRoom,
    recipient?.handle,
    navigation,
  ])

  const handleJoinRoom = useCallback(() => {
    if (!theirRoomSlug) return

    navigation.navigate('P2pCall', {
      callId: theirRoomSlug.replace('p2p-', ''),
    })
  }, [theirRoomSlug, navigation])

  const handleRejoinRoom = useCallback(() => {
    if (!myRoomSlug) return

    navigation.navigate('P2pCall', {
      callId: myRoomSlug.replace('p2p-', ''),
    })
  }, [myRoomSlug, navigation])

  // Determine button state
  if (myRoomSlug) {
    // I started a room
    return (
      <View style={styles.container}>
        <Pressable
          style={[styles.button, {backgroundColor: t.palette.primary_500}]}
          onPress={handleRejoinRoom}>
          <Text style={[a.text_xs, a.font_bold, {color: '#fff'}]}>
            In private room
          </Text>
        </Pressable>
      </View>
    )
  }

  if (theirRoomSlug) {
    // They started a room
    return (
      <View style={styles.container}>
        <Pressable
          style={[styles.button, {backgroundColor: '#22c55e'}]}
          onPress={handleJoinRoom}>
          <Text style={[a.text_xs, a.font_bold, {color: '#fff'}]}>
            Join private room
          </Text>
        </Pressable>
      </View>
    )
  }

  // Default: no room active
  return (
    <View style={styles.container}>
      <Pressable
        style={[
          styles.button,
          {backgroundColor: t.atoms.bg_contrast_25.backgroundColor},
        ]}
        onPress={handleStartRoom}
        disabled={isCreating}>
        <Text
          style={[
            a.text_xs,
            a.font_bold,
            {color: t.palette.contrast_700},
          ]}>
          {isCreating ? 'Creating...' : 'Start private room'}
        </Text>
      </Pressable>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  button: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
})
